import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { MotionConfig } from "framer-motion";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import RedesignPage from "@/redesign/RedesignPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminSetup from "@/pages/AdminSetup";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminGallery from "@/pages/AdminGallery";
import AdminBlog from "@/pages/AdminBlog";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminLeads from "@/pages/AdminLeads";
import AdminEmails from "@/pages/AdminEmails";
import AdminPassword from "@/pages/AdminPassword";
import AdminSettings from "@/pages/AdminSettings";
import AdminLayout from "@/components/AdminLayout";
import { Toaster } from "@/components/ui/sonner";

import { API } from "@/lib/api";
export { API } from "@/lib/api";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Scroll to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (pathname === '/admin' || pathname.startsWith('/admin/')) {
      ['robots', 'googlebot'].forEach(name => {
        let tag = document.head.querySelector(`meta[name="${name}"]`);
        if (!tag) { tag = document.createElement('meta'); tag.name = name; document.head.appendChild(tag); }
        tag.content = 'noindex, nofollow';
      });
    }
    if (window.location.hash) requestAnimationFrame(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView());
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setAdmin(res.data)).catch(() => {
        setToken(null);
        setAdmin(null);
        localStorage.removeItem("admin_token");
      });
    }
  }, [token]);

  const login = (newToken, adminData) => {
    setToken(newToken);
    setAdmin(adminData);
    localStorage.setItem("admin_token", newToken);
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    localStorage.removeItem("admin_token");
  };

  return (
    <MotionConfig reducedMotion="user">
    <AuthContext.Provider value={{ token, admin, login, logout }}>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/gallery" element={
            <ProtectedRoute>
              <AdminLayout><AdminGallery /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/blog" element={
            <ProtectedRoute>
              <AdminLayout><AdminBlog /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/testimonials" element={
            <ProtectedRoute>
              <AdminLayout><AdminTestimonials /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/leads" element={
            <ProtectedRoute>
              <AdminLayout><AdminLeads /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/emails" element={
            <ProtectedRoute>
              <AdminLayout><AdminEmails /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/password" element={
            <ProtectedRoute>
              <AdminLayout><AdminPassword /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute>
              <AdminLayout><AdminSettings /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="*" element={<RedesignPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
    </MotionConfig>
  );
}

export default App;
